import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Heart, Repeat } from "lucide-react";
import { currency } from "../lib/format";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const AMOUNTS = [25, 50, 100, 250, 500];

export default function DonationPage() {
    const { user } = useAuth();
    const [amt, setAmt] = useState(50);
    const [custom, setCustom] = useState("");
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [stats, setStats] = useState(null);
    const [recurring, setRecurring] = useState(false);

    useEffect(() => { api.get("/donations/stats").then((r) => setStats(r.data)); }, []);
    useEffect(() => { if (user) { setName(user.name); setEmail(user.email); } }, [user]);

    async function submit() {
        const amount = parseFloat(custom || amt);
        if (!amount || amount <= 0) return toast.error("Enter a valid amount");
        if (!name.trim()) return toast.error("Please enter your name");
        if (recurring && !email.trim()) return toast.error("Email is required for recurring gifts");
        setBusy(true);
        try {
            const endpoint = recurring ? "/payments/checkout-recurring" : "/payments/checkout";
            const payload = recurring
                ? { amount, donor_name: name, email, message: message || null, origin_url: window.location.origin, interval: "month" }
                : { amount, donor_name: name, email: email || null, message: message || null, origin_url: window.location.origin };
            const { data } = await api.post(endpoint, payload);
            if (data.checkout_url) { window.location.href = data.checkout_url; return; }
            toast.error("Could not start checkout");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not process donation");
        }
        setBusy(false);
    }

    return (
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14" data-testid="donation-page">
            <div className="grid lg:grid-cols-2 gap-14 items-start">
                <div>
                    <div className="eyebrow mb-3">Donate</div>
                    <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">Fund the rescue.<br />Change a life today.</h1>
                    <p className="mt-4 text-[var(--rl-text-2)] max-w-md">100% of donations flow to the RescueLink Global Fund — used for medical care, international transport, and boarding for at-risk dogs on every continent.</p>
                    {stats && (
                        <div className="mt-8 grid grid-cols-2 gap-4 max-w-sm">
                            <Stat label="Raised" value={currency(stats.total)} />
                            <Stat label="Donors" value={stats.count} />
                        </div>
                    )}
                    <div className="mt-8 rounded-xl border border-[var(--rl-border)] p-4 bg-[var(--rl-cream)] text-xs text-[var(--rl-text-2)]">
                        <strong className="uppercase tracking-widest text-[10px]">Powered by Stripe</strong> — test mode active. Use test card <span className="font-mono">4242 4242 4242 4242</span>, any future date, any CVC.
                    </div>
                </div>

                <div className="card-warm rounded-2xl p-8">
                    <>
                            {/* One-time / Recurring toggle */}
                            <div className="grid grid-cols-2 gap-1 p-1 mb-5 rounded-full bg-[var(--rl-cream)] border border-[var(--rl-border)]" data-testid="frequency-toggle">
                                <button onClick={() => setRecurring(false)}
                                        data-testid="freq-one-time"
                                        className={`h-9 rounded-full text-sm font-semibold transition ${!recurring ? "bg-[var(--rl-surface)] shadow-sm text-[var(--rl-text)]" : "text-[var(--rl-text-2)]"}`}>
                                    One-time
                                </button>
                                <button onClick={() => setRecurring(true)}
                                        data-testid="freq-monthly"
                                        className={`h-9 rounded-full text-sm font-semibold transition inline-flex items-center justify-center gap-1.5 ${recurring ? "bg-[var(--rl-surface)] shadow-sm text-[var(--rl-text)]" : "text-[var(--rl-text-2)]"}`}>
                                    <Repeat className="w-3.5 h-3.5" /> Give monthly
                                </button>
                            </div>

                            <div className="eyebrow mb-3">Amount{recurring ? " (per month)" : ""}</div>
                            <div className="grid grid-cols-3 gap-2">
                                {AMOUNTS.map((a) => (
                                    <button key={a} onClick={() => { setAmt(a); setCustom(""); }}
                                            data-testid={`donation-amt-${a}`}
                                            className={`h-12 rounded-lg border font-serif text-xl ${amt === a && !custom ? "border-[var(--rl-primary)] bg-[#fff5f3] text-[var(--rl-text)]" : "border-[var(--rl-border)] hover:bg-[var(--rl-cream)]"}`}>
                                        ${a}
                                    </button>
                                ))}
                            </div>
                            <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount"
                                   className="mt-3 border-[var(--rl-border)] h-11" data-testid="donation-custom" />
                            <div className="mt-5 grid gap-3">
                                <div>
                                    <label className="eyebrow">Your name</label>
                                    <Input value={name} onChange={(e) => setName(e.target.value)}
                                           className="mt-1.5 border-[var(--rl-border)]" data-testid="donation-name" />
                                </div>
                                <div>
                                    <label className="eyebrow">Email (optional)</label>
                                    <Input value={email} onChange={(e) => setEmail(e.target.value)}
                                           className="mt-1.5 border-[var(--rl-border)]" data-testid="donation-email" />
                                </div>
                                <div>
                                    <label className="eyebrow">Note (optional)</label>
                                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                                              placeholder="In memory of…" className="mt-1.5 border-[var(--rl-border)]" data-testid="donation-message" />
                                </div>
                            </div>
                            <Button onClick={submit} disabled={busy} className="btn-primary w-full rounded-full h-12 mt-6" data-testid="donation-submit">
                                {busy ? "Redirecting to Stripe…" : recurring
                                    ? <>Sustain with {currency(parseFloat(custom || amt))}/month <Repeat className="w-4 h-4 ml-2" /></>
                                    : <>Give {currency(parseFloat(custom || amt))} securely <Heart className="w-4 h-4 ml-2" /></>}
                            </Button>
                            <div className="mt-4 rounded-lg bg-[var(--rl-cream)] p-3 text-xs text-[var(--rl-text-2)] text-center">
                                <Link to="/corporate" className="font-semibold text-[var(--rl-primary)] hover:underline">Corporate giving?</Link> Donate stock, DAF, or crypto and skip capital gains.
                            </div>
                        </>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-[var(--rl-border)] rounded-lg p-4">
            <div className="font-serif text-3xl">{value}</div>
            <div className="eyebrow mt-1">{label}</div>
        </div>
    );
}
